import { useEffect, useState } from "react";
import type { CoreConversationWithMessagesModel } from "../../api/api";
import { isAgentProgress } from "../Components/Chat/chatProgressPolicy";

const AGENT_TYPING_TIMEOUT_MS = 40_000;

/** Shared response activity, independent of whether detailed agent progress is visible. */
export function useAgentTyping(conversation?: CoreConversationWithMessagesModel, awaitingSince?: number) {
  const [, expire] = useState(0);
  const now = Date.now();
  const agents = (conversation?.participants ?? []).filter((participant) => participant.type === "Agent");
  const agentIds = new Set(agents.map((participant) => participant.id));
  const userIds = new Set((conversation?.participants ?? []).filter((participant) => participant.type === "User").map((participant) => participant.id));
  const messages = conversation?.messages ?? [];
  const latestReply = Math.max(
    0,
    ...messages
      .filter((message) => agentIds.has(message.participantId) && !isAgentProgress(message))
      .map((message) => Date.parse(message.createdDate))
      .filter(Number.isFinite)
  );
  const latestUser = Math.max(
    0,
    ...messages
      .filter((message) => userIds.has(message.participantId))
      .map((message) => Date.parse(message.createdDate))
      .filter(Number.isFinite)
  );
  const starts = agents.map((participant) => Date.parse(participant.lastMessageStarted ?? ""));
  starts.push(
    ...messages.filter((message) => agentIds.has(message.participantId) && isAgentProgress(message)).map((message) => Date.parse(message.createdDate))
  );
  // A sent message can precede the server's message-start notification and first polling response.
  if (agents.length) starts.push(Math.max(latestUser, awaitingSince ?? 0));
  const until = Math.max(
    0,
    ...starts
      .filter((started) => started > latestReply && started <= now && started + AGENT_TYPING_TIMEOUT_MS > now)
      .map((started) => started + AGENT_TYPING_TIMEOUT_MS)
  );
  useEffect(() => {
    if (!until) return;
    const timer = setTimeout(() => expire((revision) => revision + 1), Math.max(0, until - Date.now()));
    return () => clearTimeout(timer);
  }, [until]);
  if (conversation?.responseStatus === "queued" || conversation?.responseStatus === "responding") return true;
  if (conversation?.responseStatus === "failed" || conversation?.responseStatus === "stalled") return false;
  return until > 0;
}
